import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import MediaPreview from 'components/MediaPreview';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import StatusLabel from 'components/status-label/StatusLabel';
import { withModal } from 'components/modal/ModalHOC';

import ContentApprovalGalleryModalAssetWithOverlay from './ContentApprovalGalleryModalAssetWithOverlay';
import ContentApprovalContext from './ContentApprovalContext';

import { t } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { CONTENT_APPROVAL_STATUS_LABEL_MAP } from 'utils/contentApproval';

import type { ClientApprovalBucketItem, ClientApprovalBucketItemRaw, ContentApprovalContextValue, GalleryModalItem } from 'types';

import type { SyntheticEvent } from 'react';

type Props = {
  item: ClientApprovalBucketItem | ClientApprovalBucketItemRaw,
  lookName?: string,
  galleryItems: Array<GalleryModalItem>,
  galleryItemIndex: number,
  showOverlay?: boolean,
  onUnsortedItemSelect?: (a: SyntheticEvent<HTMLElement>) => void
};

const DivWithGalleryModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

class ContentApprovalAsset extends React.PureComponent<Props> {
  renderStatusMarker(currentItem: ClientApprovalBucketItem, type: 'asset' | 'thumbnail'): React.ReactNode {
    const status = currentItem.approvalStatus;

    const thumbnailClasses = classnames('content-approval-asset__thumbnail-overlay', {
      'content-approval-asset__thumbnail-overlay--looks': true
    });

    const thumbnailStatusClasses = classnames('content-approval-asset__thumbnail-overlay-status', {
      [`content-approval-asset__thumbnail-overlay-status--${currentItem.approvalStatus}`]: true
    });

    if (type === 'thumbnail') {
      return (
        <div className={thumbnailClasses}>
          <div
            className={thumbnailStatusClasses}
            title={t(`content_approval.${status}`)}
          />
        </div>
      );
    }

    return (
      <StatusLabel
        type={CONTENT_APPROVAL_STATUS_LABEL_MAP[status]}
        size="large"
        background="transparent"
        label={t(`content_approval.${status}`)}
      />
    );
  }

  renderSelectedIcon(currentItem: ClientApprovalBucketItem): React.ReactNode {
    const thumbnailClasses = classnames('content-approval-asset__thumbnail-overlay', {
      'content-approval-asset__thumbnail-overlay--unsorted': true
    });

    return (
      <div
        className={thumbnailClasses}
        title={t('content_approval.gallery_modal.item_selected', {
          mediaType: t(`content_approval.gallery_modal.${currentItem.mediaType}`)
        })}
      >
        <SVGDynamicIcon
          name="checkmark-inside-circle-white-bg-custom-fg"
          width={24}
          height={24}
          customColor="primary"
        />
      </div>
    );
  }

  // see `ContentApprovalContext.Provider` for notes on why this is necessary
  renderModalThumbnailOverlay = (selectedItem: GalleryModalItem): React.ReactNode | undefined | null => (
    <ContentApprovalContext.Consumer key={selectedItem.id}>
      {
        (data: ContentApprovalContextValue | null): React.ReactNode | undefined | null => {
          if (isNil(data)) {
            return null;
          }

          const currentItem = data.items.find(item => selectedItem.id === item.id);

          if (isNil(currentItem)) {
            return null;
          }

          if (data.type === 'unsorted') {
            return currentItem.isSelected && this.renderSelectedIcon(currentItem);
          }

          return this.renderStatusMarker(currentItem, 'thumbnail');
        }
      }
    </ContentApprovalContext.Consumer>
  );

  renderModalAssetOverlayBody(selectedItem: GalleryModalItem, currentPosition: number): React.ReactNode | undefined | null {
    const { onUnsortedItemSelect } = this.props;

    // see `ContentApprovalContext.Provider` for notes on why this is necessary
    return (
      <ContentApprovalContext.Consumer key={selectedItem.id}>
        {
          (data: ContentApprovalContextValue | null): React.ReactNode | undefined | null => {
            if (isNil(data)) {
              return null;
            }

            const currentItem = data.items.find(item => selectedItem.id === item.id);

            if (isNil(currentItem)) {
              return null;
            }

            return data.type === 'unsorted' ?
              <div
                className="content-approval-asset__overlay"
                onClick={onUnsortedItemSelect}
                data-index={currentPosition}
              >
                <div className="content-approval-asset__overlay-checkbox-container">
                  <InputCheckbox
                    key={`content-approval-asset__overlay-checkbox-${currentItem.id}`}
                    id={`content-approval-asset__overlay-checkbox-${currentItem.id}`}
                    name="content-approval-asset-overlay-checkbox"
                    value={String(currentItem.isSelected)}
                    checked={currentItem.isSelected}
                    label={t('content_approval.gallery_modal.select_item', {
                      mediaType: currentItem.mediaType
                    })}
                    labelClass="content-approval-asset__overlay-label"
                  />
                </div>
              </div> :
              <div className="content-approval-asset__overlay">
                {this.renderStatusMarker(currentItem, 'asset')}
              </div>;
          }
        }
      </ContentApprovalContext.Consumer>
    );
  }

  renderCustomModalAsset = (selectedItem: GalleryModalItem, currentPosition: number): JSX.Element => {
    const { lookName } = this.props;

    return (
      <ContentApprovalGalleryModalAssetWithOverlay
        title={{
          value: lookName || '',
          show: true
        }}
        body={this.renderModalAssetOverlayBody(selectedItem, currentPosition)}
        selectedItem={selectedItem}
        currentPosition={currentPosition}
      />
    );
  };

  renderAsset(): React.ReactNode {
    const { item } = this.props;

    return (
      <MediaPreview
        key={`content-approval-asset-${item.id}`}
        attachment={{
          ...item,
          // @ts-ignore
          format: item.mediaType || item.assetType
        }}
        width={114}
        height={114}
      />
    );
  }

  render(): React.ReactNode {
    const { galleryItems, galleryItemIndex, showOverlay } = this.props;

    return (
      <div className="content-approval-asset">
        <DivWithGalleryModal
          modalContentProps={{
            items: galleryItems,
            currentPosition: galleryItemIndex,
            renderCustomAsset: showOverlay ? this.renderCustomModalAsset : null,
            renderCustomThumbnailOverlay: showOverlay ? this.renderModalThumbnailOverlay : null
          }}
        >
          {this.renderAsset()}
        </DivWithGalleryModal>
      </div>
    );
  }
}

export default ContentApprovalAsset;
